import { Grid, Typography } from 'hephaestus/web';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type HouseholdIpsComparisonRow = {
  key: string;
  current: ReactNode;
  proposed: ReactNode;
};

export type HouseholdIpsComparisonProps = {
  rows: HouseholdIpsComparisonRow[];
  testId: string;
};

/**
 * The household's IPS as it stands beside the IPS being proposed. Each field
 * is a row holding both models, so a side that runs taller than the other
 * cannot push everything below it out of step — which is the whole reason for
 * reading them side by side. On a narrow screen the pair stacks, current
 * first.
 */
export const HouseholdIpsComparison = ({
  rows,
  testId,
}: HouseholdIpsComparisonProps) => {
  const { t } = useTranslation('workflowCompletions');

  const heading = (label: string, side: string) => (
    <Grid data-testid={`${testId}-${side}-heading`} size={{ xs: 12, md: 6 }}>
      <Typography variant='titleMedium'>{label}</Typography>
    </Grid>
  );

  // Both sides of a row are stretched to the height of the taller, so a field
  // that fills what it is given ends level with the one it is read against.
  const cell = (content: ReactNode, key: string, side: string) => (
    <Grid
      data-testid={`${testId}-${side}-${key}`}
      sx={{ display: 'flex', flexDirection: 'column' }}
      size={{ xs: 12, md: 6 }}
    >
      {content}
    </Grid>
  );

  return (
    <Grid container spacing={3} data-testid={testId}>
      {heading(t('householdModelPortfolioIps.current'), 'current')}
      {heading(t('householdModelPortfolioIps.proposed'), 'proposed')}

      {rows.map(({ key, current, proposed }) => (
        <Grid container spacing={3} key={key} size={12}>
          {cell(current, key, 'current')}
          {cell(proposed, key, 'proposed')}
        </Grid>
      ))}
    </Grid>
  );
};

export default HouseholdIpsComparison;
